/**
 * ============================================================================
 * DASHBOARD PAGE - Main User Dashboard
 * ============================================================================
 *
 * Green/black theme – hero, bento stats, wallet/verification/quick action cards
 */

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../providers';
import { useWalletStore, useTransactionStore, useUIStore } from '../store';
import { useWallet, useTransaction, useUser } from '../hooks';
import { useToast } from '../providers';
import { useI18n } from '../providers';
import TransactionCard from '../components/transaction/TransactionCard';
import WalletBalance from '../components/wallet/WalletBalance';
import Spinner from '../components/common/Spinner';
import { UserDashboardLayout } from '../components/user';
import '../styles/landing.css';
import '../styles/animations.css';
import '../styles/global.css';

/**
 * Dashboard Component
 * @returns {JSX.Element}
 */
const Dashboard = () => {
  const navigate = useNavigate();
  const { user: authUser, isAuthenticated } = useAuthContext();
  const { showError } = useToast();
  const { formatCurrency, language } = useI18n();

  const { wallet: storeWallet, balance: storeBalance } = useWalletStore();
  const { wallet, balance, loading: walletLoading, loadWallet } = useWallet();
  const { transactions, loading: transactionsLoading, listTransactions } = useTransaction();
  const { user: profileUser, stats, loading: userLoading, loadMyProfile, loadStats } = useUser();

  const displayWallet = wallet ?? storeWallet;
  const displayBalance = balance ?? storeBalance;
  const displayUser = profileUser ?? authUser;
  const recentTransactions = Array.isArray(transactions) ? transactions.slice(0, 5) : [];

  const [isLoading, setIsLoading] = useState(true);
  const [statsInView, setStatsInView] = useState(false);
  const statsRef = useRef(null);

  // Redirect if not authenticated; clear loading so we don't get stuck
  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoading(false);
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Clear auth toasts when dashboard mounts so "Verification successful" etc. don't stick
  useEffect(() => {
    if (isAuthenticated) {
      useUIStore.getState().clearToasts();
    }
  }, [isAuthenticated]);

  // Load data once when authenticated (stable deps: only isAuthenticated to avoid refetch loops)
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [walletData, txList] = await Promise.all([
          loadWallet(),
          listTransactions({ limit: 5, sortBy: 'created_at', sortOrder: 'desc' }),
        ]);
        if (cancelled) return;
        if (walletData) useWalletStore.getState().setWallet(walletData);
        if (Array.isArray(txList)) useTransactionStore.getState().setTransactions(txList);
        await loadMyProfile();
        if (cancelled) return;
        if (typeof loadStats === 'function') await loadStats();
      } catch (error) {
        if (!cancelled) {
          showError('Failed to load dashboard data');
          console.error('Dashboard load error:', error);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    loadData();
    return () => { cancelled = true; };
    // Intentionally only isAuthenticated: loadData uses stable store setters and hooks from closure
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Never hang forever: if still loading after 12s, show the dashboard anyway
  useEffect(() => {
    if (!isAuthenticated) return;
    const t = setTimeout(() => {
      setIsLoading((prev) => (prev ? false : prev));
    }, 12000);
    return () => clearTimeout(t);
  }, [isAuthenticated]);

  // Intersection observer for animations
  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => e.isIntersecting && setStatsInView(true)); },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (!isAuthenticated) {
    return null;
  }

  // Only block on initial load; once loadData() finishes (isLoading false) show the dashboard
  const showLoadingSpinner = isLoading;
  if (showLoadingSpinner) {
    return (
      <UserDashboardLayout hideFooter>
        <div className="dashboard-wt dashboard-wt-loading">
          <div className="dashboard-wt-hero-inner">
            <Spinner size="lg" />
            <p className="mt-4 text-[#a3a3a3]">
              {language === 'fr' ? 'Chargement du tableau de bord...' : 'Loading your dashboard...'}
            </p>
          </div>
        </div>
      </UserDashboardLayout>
    );
  }

  const verificationLevel = displayUser?.verificationLevel || 'unverified';

  const verificationBadge = {
    verified: {
      text: 'Level 3 Verified',
      icon: '⭐⭐⭐',
      description: language === 'fr' ? 'Compte entièrement vérifié' : 'Fully verified account',
    },
    pending: {
      text: 'Level 2 Pending',
      icon: '⭐⭐',
      description: language === 'fr' ? 'Vérification en cours' : 'Verification in progress',
    },
    unverified: {
      text: 'Level 1 Unverified',
      icon: '⭐',
      description: language === 'fr' ? 'Complétez la vérification pour débloquer les fonctionnalités' : 'Complete verification to unlock features',
    },
  };

  const badge = verificationBadge[verificationLevel] || verificationBadge.unverified;

  const quickStats = [
    {
      icon: '📊',
      label: language === 'fr' ? 'Transactions' : 'Transactions',
      value: stats?.totalTransactions ?? 0,
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: '✅',
      label: language === 'fr' ? 'Taux de Réussite' : 'Success Rate',
      value: `${stats?.successRate ?? 0}%`,
      color: 'from-green-500 to-emerald-500',
    },
    {
      icon: '⭐',
      label: language === 'fr' ? 'Note Moyenne' : 'Avg Rating',
      value: stats?.averageRating?.toFixed(1) ?? '0.0',
      color: 'from-yellow-500 to-orange-500',
    },
    {
      icon: '💰',
      label: language === 'fr' ? 'Volume Total' : 'Total Volume',
      value: formatCurrency(stats?.totalVolume ?? 0, displayWallet?.currency || 'XAF'),
      color: 'from-purple-500 to-pink-500',
    },
  ];

  return (
    <UserDashboardLayout hideFooter>
      <div className="dashboard-wt dashboard-wt-fit-viewport">
        {/* Hero Header */}
        <section className="dashboard-wt-hero">
          <div className="dashboard-wt-hero-bg" aria-hidden="true" />
          <div className="dashboard-wt-hero-grid" aria-hidden="true" />
          <div className="dashboard-wt-hero-noise" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-1" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-2" aria-hidden="true" />
          
          <div className="dashboard-wt-hero-inner">
            <h1 className="dashboard-wt-hero-title">
              {language === 'fr' ? 'Bienvenue,' : 'Welcome,'} <span>{displayUser?.fullName?.split(' ')[0] || authUser?.fullName?.split(' ')[0] || 'User'}</span>
            </h1>
            <p className="dashboard-wt-hero-sub">
              {language === 'fr'
                ? 'Gérez vos transactions sécurisées en un seul endroit'
                : 'Manage your secure transactions in one place'}
            </p>
          </div>
        </section>

        {/* Quick Stats - Bento Grid */}
        <section className="dashboard-wt-section">
          <div className="dashboard-wt-section-inner">
            <div className={`dashboard-wt-stats ${statsInView ? 'is-inview' : ''}`} ref={statsRef}>
              {quickStats.map((stat, i) => (
                <div
                  key={i}
                  className="dashboard-wt-stat-card"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="dashboard-wt-stat-icon">
                    <span>{stat.icon}</span>
                  </div>
                  <div className="dashboard-wt-stat-content">
                    <div className="dashboard-wt-stat-value">{stat.value}</div>
                    <div className="dashboard-wt-stat-label">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Main Content Grid */}
        <section className="dashboard-wt-section dashboard-wt-section-alt">
          <div className="dashboard-wt-section-inner">
            <div className="dashboard-wt-grid">
              {/* Wallet Card - Large */}
              <div className="dashboard-wt-card dashboard-wt-card-large">
                <div className="dashboard-wt-card-header">
                  <h2 className="dashboard-wt-card-title">
                    {language === 'fr' ? 'Portefeuille' : 'Wallet'}
                  </h2>
                </div>
                <WalletBalance />
                <div className="dashboard-wt-card-footer">
                  <button
                    type="button"
                    className="dashboard-wt-btn-secondary"
                    onClick={() => navigate('/wallet')}
                  >
                    {language === 'fr' ? 'Voir les détails' : 'View Details'} →
                  </button>
                </div>
              </div>

              {/* Verification Card */}
              <div className="dashboard-wt-card">
                <div className="dashboard-wt-card-header">
                  <h2 className="dashboard-wt-card-title">
                    {language === 'fr' ? 'Vérification' : 'Verification'}
                  </h2>
                </div>
                <div className="dashboard-wt-card-content">
                  <div className="dashboard-wt-verification-badge">
                    <span className="dashboard-wt-verification-icon">{badge.icon}</span>
                    <span className="dashboard-wt-verification-text">{badge.text}</span>
                  </div>
                  <p className="dashboard-wt-verification-desc">{badge.description}</p>
                  {verificationLevel !== 'verified' && (
                    <button
                      type="button"
                      className="dashboard-wt-btn-primary"
                      onClick={() => navigate('/verification')}
                    >
                      {language === 'fr' ? 'Compléter la vérification' : 'Complete Verification'} →
                    </button>
                  )}
                </div>
              </div>

              {/* Quick Action Card */}
              <div className="dashboard-wt-card dashboard-wt-card-featured">
                <div className="dashboard-wt-card-header">
                  <h2 className="dashboard-wt-card-title">
                    {language === 'fr' ? 'Créer une Transaction' : 'Create Transaction'}
                  </h2>
                </div>
                <div className="dashboard-wt-card-content">
                  <p className="dashboard-wt-card-desc">
                    {language === 'fr'
                      ? 'Créez, partagez, sécurisez — en 3 étapes simples.'
                      : 'Create, share, secure — in 3 simple steps.'}
                  </p>
                  <button
                    type="button"
                    className="dashboard-wt-btn-primary dashboard-wt-btn-large"
                    onClick={() => navigate('/transactions/create')}
                  >
                    <span className="dashboard-wt-btn-icon">➕</span>
                    {language === 'fr' ? 'Nouvelle Transaction' : 'New Transaction'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Recent Transactions */}
        <section className="dashboard-wt-section">
          <div className="dashboard-wt-section-inner">
            <div className="dashboard-wt-section-head">
              <h2 className="dashboard-wt-section-title">
                {language === 'fr' ? 'Transactions Récentes' : 'Recent Transactions'}
              </h2>
              {recentTransactions.length > 0 && (
                <button
                  type="button"
                  className="dashboard-wt-btn-link"
                  onClick={() => navigate('/transactions')}
                >
                  {language === 'fr' ? 'Voir tout' : 'View All'} →
                </button>
              )}
            </div>

            {recentTransactions.length === 0 ? (
              <div className="dashboard-wt-empty">
                <div className="dashboard-wt-empty-icon">📦</div>
                <h3 className="dashboard-wt-empty-title">
                  {language === 'fr' ? 'Aucune transaction' : 'No transactions yet'}
                </h3>
                <p className="dashboard-wt-empty-desc">
                  {language === 'fr'
                    ? 'Commencez par créer votre première transaction. C\'est simple!'
                    : 'Get started by creating your first transaction. It\'s simple!'}
                </p>
                <button
                  type="button"
                  className="dashboard-wt-btn-primary"
                  onClick={() => navigate('/transactions/create')}
                >
                  {language === 'fr' ? 'Créer une Transaction' : 'Create Transaction'}
                </button>
              </div>
            ) : (
              <div className="dashboard-wt-transactions">
                {recentTransactions.map((transaction, i) => (
                  <div
                    key={transaction.id}
                    style={{ animationDelay: `${i * 0.1}s` }}
                    className="dashboard-wt-transaction-wrapper"
                  >
                    <TransactionCard
                      transaction={transaction}
                      onClick={() => navigate(`/transactions/${transaction.id}`)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </UserDashboardLayout>
  );
};

export default Dashboard;
